import type { EChartsOption } from 'echarts';

/**
 * `option` do ECharts que o backend monta na leitura (contrato §2.14, 15/10/2026): o FE usa o
 * `option` como veio e injeta só as duas funções que não trafegam em JSON, o `formatter` do eixo
 * Y e o `valueFormatter` do tooltip. Nada de merge profundo nem de sobrescrever outra chave: um
 * ajuste visual feito no backend vale sem deploy do FE.
 */

/** Formatador de número pt-BR de um gráfico (ex.: `formatarMW`, `formatNumberBR`). */
export type FormatadorDeNumero = (valor: number) => string;

/** `tooltip.valueFormatter`: o ECharts passa o valor do ponto (número, texto, `null` na lacuna). */
export type ValueFormatter = (valor: unknown) => string;

export interface FormatadoresDoGrafico {
  /** `tooltip.valueFormatter`, em todos os gráficos. */
  tooltip: ValueFormatter;
  /** `yAxis.axisLabel.formatter`, onde o FE formata o eixo Y (séries, Visão Geral e Balanço). */
  eixoY?: FormatadorDeNumero;
}

type Objeto = Record<string, unknown>;

const ehObjeto = (valor: unknown): valor is Objeto => (
  typeof valor === 'object' && valor !== null && !Array.isArray(valor)
);

/**
 * `valueFormatter` do tooltip a partir do formatador do gráfico: formata só número finito;
 * lacuna (`null`/`undefined`) e número não finito viram `-`, como o padrão do ECharts que a tela
 * já mostrava (decisão do Marcelo, 16/10/2026), e texto passa como veio. Crie no escopo do módulo:
 * com a mesma referência entre renders, o `echarts-for-react` vê o `option` igual e não chama o
 * `setOption` de novo.
 */
export const criarValueFormatter = (formatar: FormatadorDeNumero): ValueFormatter => (valor) => {
  if (typeof valor === 'number') return Number.isFinite(valor) ? formatar(valor) : '-';
  if (valor === null || valor === undefined) return '-';
  return String(valor);
};

/** Aplica `injetar` ao componente do `option`, objeto ou lista de objetos, sem mutar. */
const emCadaItem = (componente: unknown, injetar: (item: Objeto) => Objeto): unknown => {
  if (Array.isArray(componente)) {
    return componente.map((item) => (ehObjeto(item) ? injetar(item) : item));
  }
  return ehObjeto(componente) ? injetar(componente) : componente;
};

/**
 * Cópia rasa do `option` com `tooltip.valueFormatter` e, se pedido, `yAxis.axisLabel.formatter`
 * (`yAxis` objeto ou lista). Não muta a entrada (o dado do RTK Query vem congelado) e não toca em
 * mais nada: o resto do `option` segue com as mesmas referências. Não cria `tooltip` nem `yAxis`
 * que o `option` não tenha, porque um `tooltip` criado aqui ligaria o tooltip padrão do ECharts
 * onde o backend não pôs nenhum.
 */
export function comFormatadores<T extends EChartsOption>(
  option: T,
  { tooltip, eixoY }: FormatadoresDoGrafico,
): T {
  const copia: Objeto = { ...option };
  if (option.tooltip !== undefined) {
    copia.tooltip = emCadaItem(option.tooltip, (item) => ({ ...item, valueFormatter: tooltip }));
  }
  if (eixoY && option.yAxis !== undefined) {
    copia.yAxis = emCadaItem(option.yAxis, (eixo) => ({
      ...eixo,
      axisLabel: { ...(ehObjeto(eixo.axisLabel) ? eixo.axisLabel : {}), formatter: eixoY },
    }));
  }
  return copia as T;
}
